import speak from '@assets/icons/speak.svg';
import { ErrorMessage, TaAutocomplete } from '@components/shared';
import { NotificationSnackbar } from '@components/shared/notificationSnackbar';
import { TaModal } from '@components/shared/TaModal/TaModal';
import { EditableCard } from '@features/dashboard/specialistDashboard/editableCard/editableCard';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import { Chip } from '@mui/material';
import { mapToDictionary } from '@utils/util.functions';
import { Dictionary, Nullable } from '@utils/util.types';
import classNames from 'classnames';
import { useFormik } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCountries, useCountriesStore } from 'services/countries';
import { useLanguages, useLanguagesStore } from 'services/languages';
import { useUpdateUser, useUsersStore } from 'services/users';
import * as Yup from 'yup';

const flagCDN = 'https://flagcdn.com/w20/';

const initialValues = {
	country: null,
	userLanguages: [],
};

interface OfferFormValues {
	country: Nullable<Dictionary>;
	userLanguages: Dictionary[];
}

const CommunicationValidationSchema = Yup.object().shape({
	country: Yup.object().required('indicateCountry'),
	userLanguages: Yup.array().min(1, 'indicateLanguage'),
});

interface CommunicationProps {
	readonly?: boolean;
}

export const FarmerCommunication = ({ readonly = false }: CommunicationProps) => {
	const { t } = useTranslation();
	const { user } = useUsersStore();
	const { countries } = useCountriesStore();
	const { languages } = useLanguagesStore();
	const [open, setOpen] = useState(false);
	const [snackbarOpen, setSnackbarOpen] = useState(false);
	const [isLoading, setIsLoading] = useState(false);

	useCountries();
	useLanguages();
	const languagesOptions = useMemo(() => mapToDictionary(languages), [languages]);
	const mutate = useUpdateUser();

	const formik = useFormik<OfferFormValues>({
		initialValues: initialValues,
		validationSchema: CommunicationValidationSchema,
		onSubmit: async (values) => {
			setIsLoading(true);
			try {
				await mutate({
					country: values.country?.id || '',
					userLanguages: values.userLanguages.map((language) => language.id),
				});
				setOpen(false);
				setSnackbarOpen(true);
				setIsLoading(false);
			} catch (error) {
				console.error('Update failed:', error);
				setIsLoading(false);
			}
		},
	});

	const { setValues } = formik;

	useEffect(() => {
		if (user) {
			setValues({
				country:
					countries.find(
						(country) => country.id === user.country || country.name === user.country
					) || null,
				userLanguages: user.user_languages,
			});
		}
	}, [user, setValues, countries]);

	const renderCountryIcon = (country: any) => {
		if (country?.alpha_two) {
			return (
				<img
					src={`${flagCDN}${country?.alpha_two.toLowerCase()}.png`}
					alt={country?.alpha_two}
					style={{ width: 16, height: 12 }}
				/>
			);
		} else {
			return (
				<PublicOutlinedIcon
					className={classNames('text-secondary', {
						'text-error': formik.touched.country && formik.errors.country,
					})}
				/>
			);
		}
	};

	return (
		<EditableCard
			onEdit={() => setOpen(true)}
			className="responsive-farmer-card"
			title={t('communication')}
			readonly={readonly}
		>
			<div className="flex flex-col gap-[20px] pb-5">
				<div className="flex flex-wrap gap-[10px]">
					<PublicOutlinedIcon className="text-secondary" />
					<div className="w-[1px] h-[20px] bg-[#E9ECEF]"></div>
					<p className="text-[#343A40] roboto-regular text-[16px] leading-[140%]">{t('from')}</p>
					<Chip
						label={
							<div className="flex gap-[10px]">
								<img
									width="16"
									height="12"
									src={`https://flagcdn.com/w20/${user?.unicode?.alpha_two.toLowerCase()}.png`}
									alt={user?.unicode?.alpha_two}
								/>
								<p>{user?.country || ''}</p>
							</div>
						}
						variant="outlined"
						sx={{
							borderRadius: '8px',
							border: '1px solid #CED4DA',
							backgroundColor: '#F8F9FA',
							padding: '1px 10px',
							color: '#7B809A',
							fontFamily: 'Roboto',
							fontSize: '16px',
							fontStyle: 'normal',
							fontWeight: 400,
							lineHeight: '140%',
							height: '24px',
							textTransform: 'camelcase',
							'& .MuiChip-label': {
								padding: 0,
							},
						}}
					/>
				</div>
				<div className="flex gap-[10px] flex-wrap">
					<img className="text-secondary" src={speak} alt={'speak'} />
					<div className="w-[1px] h-[20px] bg-[#E9ECEF]"></div>
					<p className="text-[#343A40] roboto-regular text-[16px] leading-[140%]">{t('speak')}</p>
					{user?.user_languages.map((lang) => (
						<Chip
							key={lang.id}
							label={lang.name}
							variant="outlined"
							sx={{
								borderRadius: '8px',
								border: '1px solid #CED4DA',
								backgroundColor: '#F8F9FA',
								padding: '1px 10px',
								color: '#7B809A',
								fontFamily: 'Roboto',
								fontSize: '16px',
								fontStyle: 'normal',
								fontWeight: 400,
								lineHeight: '140%',
								height: '24px',
								textTransform: 'camelcase',
								'& .MuiChip-label': {
									padding: 0,
								},
							}}
						/>
					))}
				</div>
			</div>
			<TaModal
				save={formik.handleSubmit}
				open={open}
				onClose={() => setOpen(false)}
				saveButtonLoading={isLoading}
				disableSaveButton={isLoading}
				title={t('communication')}
				showActions
			>
				<div className="flex flex-col gap-[20px] pb-5 mt-[20px]">
					<div className="flex flex-col gap-[10px]">
						<span className="roboto-regular text-[16px] leading-[140%]">{t('from')}</span>
						<div>
							<TaAutocomplete
								required
								options={countries}
								hasFlag
								getOptionLabel={(option) => option.name}
								label={t('country')}
								value={formik.values.country as Dictionary}
								icon={renderCountryIcon(formik.values.country)}
								onChange={(event, value) => {
									formik.setFieldValue('country', value);
								}}
								error={Boolean(formik.touched.country && formik.errors.country)}
							/>
							{formik.touched.country && formik.errors.country && (
								<ErrorMessage message={t(formik.errors.country as string)} />
							)}
						</div>
					</div>
					<div className="flex flex-col gap-[10px]">
						<span className="roboto-regular text-[16px] leading-[140%]">{t('speak')}</span>
						<div>
							<TaAutocomplete
								filterSelectedOptions
								options={
									formik.values.userLanguages
										? languagesOptions.filter(
												(option) =>
													!formik.values.userLanguages.some((selected) => selected.id === option.id)
											)
										: languagesOptions
								}
								getOptionLabel={(option) => option.name}
								label={t('communicationLanguage')}
								icon={
									<img
										className={classNames('text-secondary', {
											'text-error': formik.touched.userLanguages && formik.errors.userLanguages,
										})}
										src={speak}
										alt="speak"
									/>
								}
								required
								multiple
								value={formik.values.userLanguages}
								onChange={(event, value) => formik.setFieldValue('userLanguages', value)}
								error={Boolean(formik.touched.userLanguages && formik.errors.userLanguages)}
							/>
							{formik.touched.userLanguages && formik.errors.userLanguages && (
								<ErrorMessage message={t(formik.errors.userLanguages as string)} />
							)}
						</div>
					</div>
				</div>
			</TaModal>
			<NotificationSnackbar
				open={snackbarOpen}
				onClose={() => setSnackbarOpen(false)}
				title={t('successfullyUpdated')}
				success
				description={t('profileUpdated', {
					title: t('communication'),
				})}
			/>
		</EditableCard>
	);
};
